/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { LoadingSpinnerStyle } from "@insite/mobius/LoadingSpinner";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { styledScrollbar } from "@rc/App/SharedStyles/sharedStyles";
import { css } from "styled-components";

export interface MiniCartOverlayStyles {
    miniCartContainerStyles?: InjectableCss;
    spinnerWrapper?: InjectableCss;
    miniCartHeadingStyles?: InjectableCss;
    miniCartHeadingInnerStyles?: InjectableCss;
    cartLinesHeadingStyles?: InjectableCss;
    fancyOverflowStyles?: InjectableCss;
    cartLineStyles?: InjectableCss;
    cartLineImageWrapperStyles?: InjectableCss;
    cartLineRemoveButtonStyles?: InjectableCss;
    cartLineImageStyles?: InjectableCss;
    cartLineDescriptionStyles?: InjectableCss;
    cartLineDescriptionWrapperStyles?: InjectableCss;
    cartLineOrderedQtyWrapper?: InjectableCss;
    qtyInput?: TextFieldPresentationProps;
    minicartActionButtonsStyles?: InjectableCss;
    minicartCheckoutButtons?: InjectableCss;
    minicartActionTotalStyle?: InjectableCss;
}

export const minicartStyles: MiniCartOverlayStyles = {
    miniCartContainerStyles: {
        css: css`
            width: 435px;
            height: 100%;
            min-height: 250px;
            overflow: hidden;
            position: relative;
            display: flex;
            flex-direction: column;
            background: ${getColor("common.white")};
        `,
    },
    miniCartHeadingStyles: {
        css: css`
            padding: 17px 25px 0 17px;
            margin-bottom: 12px;
        `,
    },
    miniCartHeadingInnerStyles: {
        css: css`
            padding-bottom: 5px;
            border-bottom: 1px solid ${getColor("common.ui3")};
            display: flex;
            align-items: center;
        `,
    },

    fancyOverflowStyles: {
        css: css`
            overflow: hidden;
            position: relative;
            &.show-opacity {
                .inner-wrapper {
                    &:after {
                        position: absolute;
                        bottom: 0;
                        left: 0;
                        content: "";
                        height: 40px;
                        width: calc(100% - 16px);
                        background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #ffffff 100%);
                        pointer-events: none;
                    }

                    >div:last-child {
                        .description-wrapper {
                            z-index: 3;
                        }
                        &:after {
                            content: "";
                            position: absolute;
                            bottom: 5px;
                            left: 0;
                            height: 40px;
                            width: calc(100% - 16px);
                            background: #fff;
                            pointer-events: none;
                            z-index: 2;
                        }
                    }
                }
            }

            .inner-wrapper {
                max-height: 400px;
                padding-right: 16px;
                padding-left: 17px;
                height: 100%;
                ${styledScrollbar(5, "secondary")};
            }

            &.empty-cart {
                display: flex;
                justify-content: center;
                align-items: center;
            }
        `,
    },
    spinnerWrapper: {
        css: css`
            position: absolute;
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            display: flex;
            align-items: center;
            justify-content: center;

            &:before {
                content: "";
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                background: ${getColor("primary.contrast")};
                opacity: 0.75;
                z-index: 1;
            }

            & ${/* sc-selector */ LoadingSpinnerStyle} {
                z-index: 1300;
            }
        `,
    },
    cartLineStyles: {
        css: css`
            width: 100%;
            position: relative;
            padding: 0 30px 11px 0;
            display: flex;
            position: relative;

            &:not(:last-child) {
                border-bottom: 1px solid ${getColor("common.ui3")};
                margin-bottom: 11px;
            }
        `,
    },
    cartLineImageWrapperStyles: {
        css: css`
            width: 64px;
            min-width: 64px;
            height: 64px;
            margin-right: 12px;
        `,
    },
    cartLineImageStyles: {
        css: css``,
    },
    cartLineRemoveButtonStyles: {
        css: css`
            position: absolute;
            right: 0;
            top: 0;
            &:hover {
                svg {
                    color: ${getColor("primary")};
                }
            }
        `,
    },

    cartLineDescriptionWrapperStyles: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },

    cartLineDescriptionStyles: {
        css: css`
            margin-bottom: 4px;
            &:hover {
                ${TypographyStyle} {
                    color: ${getColor("primary")};
                    text-decoration: underline;
                }
            }
        `,
    },
    qtyInput: {
        cssOverrides: {
            formField: css`
                width: 45px;
                &&& input {
                    max-width: 45px;
                    width: 45px;
                    min-height: 30px;
                    border: 1px solid ${getColor("text.main")} !important;
                    border-radius: 1px !important;
                    text-align: center;
                    padding: 0 2px 0 0 !important;
                    margin-left: 0;
                    margin-right: 0;
                    font-size: 16px;
                    line-height: 17px;
                }
            `,
        },
    },
    cartLineOrderedQtyWrapper: {
        css: css`
            display: flex;
            width: 95px;

            .qty-action {
                width: 25px;
                height: 30px;
                display: flex;
                align-items: center;
                justify-content: center;
                &:hover {
                    cursor: pointer;
                }

                &.disabled {
                    &:hover,
                    &:focus {
                        cursor: not-allowed;
                    }
                }
            }
        `,
    },
    minicartActionButtonsStyles: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            padding: 16px;
            padding-left: 17px;
            border-top: 1px solid #e7e7e7;
        `,
    },
    minicartActionTotalStyle: {
        css: css`
            display: flex;
            justify-content: flex-end;
            margin-bottom: 12px;
        `,
    },
    minicartCheckoutButtons: {
        css: css`
            display: flex;
            justify-content: space-between;
            button {
                min-height: 40px;
                padding: 8px 25px;
                & span {
                    line-height: 17px;
                }
            }
        `,
    },
};
const CartStyles = minicartStyles;

export default CartStyles;
